import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../Context/AuthContext';
import { Package, Plus, LogOut, Hexagon, X, CheckCircle, Trash2, Loader2, ShoppingBag,Edit } from 'lucide-react';
import './AdminDashboard.css';
import AddProductForm from '../components/admin/AddProductForm';
import OrderManagement from '../components/admin/OrderManagement';
import OrderDetails from '../components/admin/OrderDetails';
import CustomModal from '../components/common/CustomModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AdminDashboard = () => {
    const { logout } = useAuth();
    const navigate = useNavigate();

    const [showForm, setShowForm] = useState(false);
    const [showOrders, setShowOrders] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [products, setProducts] = useState([]);
    const [productCount, setProductCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [modalConfig, setModalConfig] = useState({
        isOpen: false,
        title: '',
        message: '',
        confirmText: 'OK',
        cancelText: 'Cancel',
        onConfirm: () => {},
        onCancel: () => {},
        type: 'confirm'
    });

    // Fetch the total number of products from the backend
    const fetchProductCount = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/products`);
            const data = await res.json();
            if (data.success) {
                setProducts(data.data);
                setProductCount(data.data.length);
            }
        } catch (err) {
            console.error('Error fetching product count:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProductCount();
    }, [fetchProductCount]);

    // Called by AddProductForm after a successful product submission
    const handleProductAdded = () => {
        fetchProductCount();   // refresh the count
        setShowForm(false);    // close the form
        setToastMessage('Product added successfully!');
        setToast(true);        // show success toast
        setTimeout(() => setToast(false), 3500);
    };

const handleEdit = (product) => {
  navigate(`/admin/products/edit/${product.id}`);
};

    const handleDeleteProduct = (id, name) => {
        setModalConfig({
            isOpen: true,
            title: 'Delete Product',
            message: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'confirm',
            onConfirm: async () => {
                setModalConfig(prev => ({ ...prev, isOpen: false }));
                try {
                    const res = await fetch(`${API_URL}/products/${id}`, {
                        method: 'DELETE',
                    });

                    if (res.ok) {
                        fetchProductCount();
                        setToastMessage('Product deleted successfully!');
                        setToast(true);
                        setTimeout(() => setToast(false), 3500);
                    } else {
                        showAlert('Error', 'Failed to delete product.');
                    }
                } catch (err) {
                    console.error('Error deleting product:', err);
                    showAlert('Error', 'Error deleting product.');
                }
            },
            onCancel: () => setModalConfig(prev => ({ ...prev, isOpen: false }))
        });
    };

    const showAlert = (title, message) => {
        setModalConfig({
            isOpen: true,
            title,
            message,
            confirmText: 'Close',
            type: 'alert',
            onConfirm: () => setModalConfig(prev => ({ ...prev, isOpen: false })),
            onCancel: () => setModalConfig(prev => ({ ...prev, isOpen: false }))
        });
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    return (
        <div className="admin-dashboard-container fade-in">
            {/* Success Toast */}
            {toast && (
                <div className="success-toast fade-in">
                    <CheckCircle size={18} />
                    <span>{toastMessage}</span>
                </div>
            )}

            <header className="admin-header">
                <div className="admin-logo-section">
                    <div className="admin-logo-icon">
                        <Hexagon className="icon-main" size={24} />
                    </div>
                    <div className="admin-logo-text">
                        <h2>Admin Dashboard</h2>
                        <span>Ithal by MR</span>
                    </div>
                </div>
                <button className="admin-logout-btn" onClick={handleLogout}>
                    <LogOut size={18} />
                    <span>Logout</span>
                </button>
            </header>

            <main className="admin-main">
                <div className="admin-welcome-section slide-up">
                    <h1 className="admin-title">Welcome to Admin Dashboard</h1>
                    <p className="admin-subtitle">Manage your boutique products</p>
                </div>

                <div className="admin-cards-container">
                    {/* Total Products Card */}
                    <div className={`admin-card stats-card slide-up ${(showForm || showOrders) ? 'shrunk' : ''}`} style={{ animationDelay: '0.1s' }}>
                        <div className="stats-card-content">
                            <div className="stats-text">
                                <p className="card-label">Total Products</p>
                                <h2 className="card-value">{productCount}</h2>
                            </div>
                            <div className="card-icon-wrapper light-pink">
                                <Package size={showForm ? 20 : 28} className="pink-icon" />
                            </div>
                        </div>
                    </div>

                    {/* Add New Product / Close Form Toggle Card */}
                    <div
                        className={`admin-card action-card gradient-bg slide-up ${showForm ? 'shrunk close-action' : ''} ${showOrders ? 'hidden-card' : ''}`}
                        style={{ animationDelay: '0.2s', display: showOrders ? 'none' : 'flex' }}
                        onClick={() => {
                            setShowForm(!showForm);
                            setShowOrders(false);
                            setSelectedOrder(null);
                        }}
                    >
                        {showForm ? (
                            <div className="close-form-content">
                                <X size={20} className="close-icon" />
                                <p className="action-text mini">Close Form</p>
                            </div>
                        ) : (
                            <>
                                <Plus size={40} className="white-icon" />
                                <p className="action-text">Add New Product</p>
                            </>
                        )}
                    </div>

                    {/* Manage Orders Card */}
                    <div
                        className={`admin-card action-card blue-gradient slide-up ${showOrders && !selectedOrder ? 'shrunk close-action' : ''} ${showForm ? 'hidden-card' : ''}`}
                        style={{ animationDelay: '0.3s', display: showForm ? 'none' : 'flex', background: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)' }}
                        onClick={() => {
                            if (showOrders && !selectedOrder) {
                                setShowOrders(false);
                            } else {
                                setShowOrders(true);
                                setShowForm(false);
                                setSelectedOrder(null);
                            }
                        }}
                    >
                        {showOrders && !selectedOrder ? (
                            <div className="close-form-content">
                                <X size={20} className="close-icon" />
                                <p className="action-text mini">Close Orders</p>
                            </div>
                        ) : (
                            <>
                                <ShoppingBag size={40} className="white-icon" />
                                <p className="action-text">Manage Orders</p>
                            </>
                        )}
                    </div>
                </div>

                {showForm && (
                    <div className="admin-form-section">
                        <AddProductForm onProductAdded={handleProductAdded} />
                    </div>
                )}

                {showOrders && !selectedOrder && (
                    <div className="admin-orders-section" style={{ marginTop: '24px' }}>
                        <OrderManagement 
                            onBack={() => setShowOrders(false)} 
                            onOrderSelect={(order) => setSelectedOrder(order)}
                            setToastMessage={setToastMessage}
                            setToast={setToast}
                        />
                    </div>
                )}

                {showOrders && selectedOrder && (
                    <div className="admin-order-details-section" style={{ marginTop: '24px' }}>
                        <OrderDetails 
                            orderId={selectedOrder.id}
                            onBack={() => setSelectedOrder(null)}
                            setToastMessage={setToastMessage}
                            setToast={setToast}
                        />
                    </div>
                )}

                {/* Product List Section */}
                {!showForm && !showOrders && (
                    <div className="admin-product-list-section fade-in">
                        <div className="list-header">
                            <h3>Manage Products</h3>
                        </div>

                        {loading ? (
                            <div className="admin-loading-state">
                                <Loader2 className="spin-icon" size={32} />
                                <p>Loading products...</p>
                            </div>
                        ) : products.length === 0 ? (
                            <div className="empty-state">No products found. Add some!</div>
                        ) : (
                            <div className="product-table-container">
                                <table className="product-table">
                                    <thead>
                                        <tr>
                                            <th>Image</th>
                                            <th>Product Name</th>
                                            <th>Category</th>
                                            <th>Price</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {products.map(product => (
                                            <tr key={product.id}>
                                                <td>
                                                    <img
                                                        src={product.image_url}
                                                        alt={product.name}
                                                        className="admin-product-thumb"
                                                        onError={(e) => { 
                                                            if (!e.target.dataset.tried) {
                                                                e.target.dataset.tried = 'true';
                                                                e.target.src = '/uploads/placeholder.jpg'; 
                                                            }
                                                        }}
                                                    />
                                                </td>
                                                <td className="product-name-cell">{product.name}</td>
                                                <td><span className="category-badge">{product.category}</span></td>
                                                <td className="price-cell">₹{product.sale_price}</td>
                                                <td>
                                                                                                                                                        <button
                                                                                                                                                            className="delete-item-btn"
                                                                                                                                                             onClick={() => handleEdit(product)}
                                                                                                                                                            title="Edit Product"
                                                                                                                                                        >
                                                                                                                                                            <Edit size={18} />
                                                                                                                                                        </button>
                                                    <button
                                                        className="delete-item-btn"
                                                        onClick={() => handleDeleteProduct(product.id, product.name)}
                                                        title="Delete Product"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>

                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </main>

            <CustomModal 
                isOpen={modalConfig.isOpen}
                title={modalConfig.title}
                message={modalConfig.message}
                confirmText={modalConfig.confirmText}
                cancelText={modalConfig.cancelText}
                onConfirm={modalConfig.onConfirm}
                onCancel={modalConfig.onCancel}
                type={modalConfig.type}
            />
        </div>
    );
};

export default AdminDashboard;
